import type {
	Auth0Client,
	Auth0ClientOptions,
	RedirectLoginOptions,
} from "@auth0/auth0-spa-js";
import axios, { type AxiosError } from "axios";

import {
	isTokenExpired,
	type LocalUser,
	useAuthStore,
} from "@/stores/useAuthStore";

const AUTH_SCOPE = "openid profile email offline_access";

type Auth0TokenResponse = {
	access_token: string;
	refresh_token?: string;
	id_token?: string;
	expires_in: number;
	token_type: string;
};

type EmailVerificationDetail = {
	code: "email_verification_required";
	email?: string;
	auth0_sub?: string;
};

type AccountDeletionInProgressDetail = {
	code: "account_deletion_in_progress";
};

type RefreshAccessTokenOptions = {
	force?: boolean;
};

let auth0Client: Auth0Client | null = null;
type RefreshContext = {
	accessToken: string | null;
	refreshToken: string | null;
	authMethod: "password" | "auth0-spa" | null;
};

let refreshPromise: {
	context: RefreshContext;
	promise: Promise<string | null>;
} | null = null;

function requireEnv(name: string): string {
	const value = (import.meta.env as Record<string, string | undefined>)[name];
	if (!value) throw new Error(`Missing ${name}`);
	return value;
}

export function getAuth0Domain() {
	return requireEnv("VITE_AUTH0_DOMAIN")
		.replace(/^https:\/\//, "")
		.replace(/\/$/, "");
}

export function getAuth0ClientId() {
	return requireEnv("VITE_AUTH0_CLIENT_ID");
}

export function getAuth0Audience() {
	return requireEnv("VITE_AUTH0_AUDIENCE");
}

export function getAuth0Connection() {
	return (
		import.meta.env.VITE_AUTH0_CONNECTION || "Username-Password-Authentication"
	);
}

function getRedirectUri() {
	return (
		import.meta.env.VITE_AUTH0_REDIRECT_URI ||
		`${window.location.origin}/auth/callback`
	);
}

function getApiBaseUrl() {
	return requireEnv("VITE_API_BASE_URL").replace(/\/$/, "");
}

async function getAuth0Client(): Promise<Auth0Client> {
	if (auth0Client) return auth0Client;

	const { Auth0Client: Auth0ClientCtor } = await import("@auth0/auth0-spa-js");
	const options: Auth0ClientOptions = {
		domain: getAuth0Domain(),
		clientId: getAuth0ClientId(),
		cacheLocation: "localstorage",
		useRefreshTokens: true,
		authorizationParams: {
			audience: getAuth0Audience(),
			redirect_uri: getRedirectUri(),
			scope: AUTH_SCOPE,
		},
	};

	auth0Client = new Auth0ClientCtor(options);
	return auth0Client;
}

export async function loginWithGoogle() {
	const client = await getAuth0Client();
	const options: RedirectLoginOptions = {
		authorizationParams: {
			audience: getAuth0Audience(),
			connection:
				import.meta.env.VITE_AUTH0_GOOGLE_CONNECTION || "google-oauth2",
			redirect_uri: getRedirectUri(),
			scope: AUTH_SCOPE,
		},
	};
	await client.loginWithRedirect(options);
}

export async function handleAuth0RedirectCallback() {
	const client = await getAuth0Client();
	await client.handleRedirectCallback();
	const accessToken = await client.getTokenSilently({
		authorizationParams: {
			audience: getAuth0Audience(),
			scope: AUTH_SCOPE,
		},
	});
	useAuthStore
		.getState()
		.setTokens({ accessToken, refreshToken: null, authMethod: "auth0-spa" });
	try {
		const user = await syncCurrentUser(accessToken);
		useAuthStore.getState().setUser(user);
		return user;
	} catch (error) {
		if (getAccountDeletionInProgressDetail(error)) {
			useAuthStore.getState().reset();
		} else {
			const verification = getEmailVerificationDetail(error);
			if (verification) {
				useAuthStore
					.getState()
					.setPendingVerification(verification.email ?? null);
			} else {
				useAuthStore.getState().reset();
			}
		}
		throw error;
	}
}

export async function loginWithPassword(email: string, password: string) {
	const response = await axios.post<Auth0TokenResponse>(
		`https://${getAuth0Domain()}/oauth/token`,
		{
			grant_type: "http://auth0.com/oauth/grant-type/password-realm",
			client_id: getAuth0ClientId(),
			audience: getAuth0Audience(),
			username: email,
			password,
			realm: getAuth0Connection(),
			scope: AUTH_SCOPE,
		},
		{ headers: { "Content-Type": "application/json" } },
	);

	useAuthStore.getState().setTokens({
		accessToken: response.data.access_token,
		refreshToken: response.data.refresh_token ?? null,
		authMethod: "password",
	});
	return response.data;
}

export async function signupWithPassword(email: string, password: string) {
	await axios.post(
		`https://${getAuth0Domain()}/dbconnections/signup`,
		{
			client_id: getAuth0ClientId(),
			email,
			password,
			connection: getAuth0Connection(),
		},
		{ headers: { "Content-Type": "application/json" } },
	);
	return loginWithPassword(email, password);
}

export async function syncCurrentUser(
	accessToken?: string,
): Promise<LocalUser> {
	const token = accessToken || useAuthStore.getState().accessToken;
	if (!token) throw new Error("Missing access token");

	try {
		const response = await axios.get<LocalUser>(
			`${getApiBaseUrl()}/api/v1/auth/me`,
			{
				headers: {
					Authorization: `Bearer ${token}`,
					...(import.meta.env.VITE_API_KEY
						? { "X-API-Key": import.meta.env.VITE_API_KEY }
						: {}),
				},
			},
		);
		return response.data;
	} catch (error) {
		if (getAccountDeletionInProgressDetail(error)) {
			useAuthStore.getState().reset();
		}
		throw error;
	}
}

export async function resendVerificationEmail() {
	const token = useAuthStore.getState().accessToken;
	if (!token) throw new Error("Missing access token");
	await axios.post(
		`${getApiBaseUrl()}/api/v1/auth/verification-email`,
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
				...(import.meta.env.VITE_API_KEY
					? { "X-API-Key": import.meta.env.VITE_API_KEY }
					: {}),
			},
		},
	);
}

export function getEmailVerificationDetail(
	error: unknown,
): EmailVerificationDetail | null {
	const axiosError = error as AxiosError<{ detail?: unknown }>;
	const detail = axiosError.response?.data?.detail;
	if (
		detail &&
		typeof detail === "object" &&
		"code" in detail &&
		(detail as { code?: unknown }).code === "email_verification_required"
	) {
		return detail as EmailVerificationDetail;
	}
	return null;
}

export function getAccountDeletionInProgressDetail(
	error: unknown,
): AccountDeletionInProgressDetail | null {
	if (!axios.isAxiosError<{ detail?: unknown }>(error)) {
		return null;
	}

	const detail = error.response?.data?.detail;
	if (
		detail &&
		typeof detail === "object" &&
		"code" in detail &&
		(detail as { code?: unknown }).code === "account_deletion_in_progress"
	) {
		return detail as AccountDeletionInProgressDetail;
	}
	return null;
}

export async function refreshAccessToken(
	options: RefreshAccessTokenOptions = {},
): Promise<string | null> {
	const initialContext = getRefreshContext();
	if (
		refreshPromise &&
		isSameRefreshContext(refreshPromise.context, initialContext)
	) {
		return refreshPromise.promise;
	}

	const promise = (async () => {
		const { accessToken, refreshToken, authMethod } = initialContext;
		if (accessToken && !options.force && !isTokenExpired(accessToken)) {
			return accessToken;
		}

		try {
			if (authMethod === "password" && refreshToken) {
				const response = await axios.post<Auth0TokenResponse>(
					`https://${getAuth0Domain()}/oauth/token`,
					{
						grant_type: "refresh_token",
						client_id: getAuth0ClientId(),
						refresh_token: refreshToken,
					},
				);
				if (!isSameRefreshContext(getRefreshContext(), initialContext)) {
					return null;
				}
				useAuthStore.getState().setTokens({
					accessToken: response.data.access_token,
					refreshToken: response.data.refresh_token ?? refreshToken,
					authMethod: "password",
				});
				return response.data.access_token;
			}

			if (authMethod === "auth0-spa") {
				const client = await getAuth0Client();
				const token = await client.getTokenSilently({
					cacheMode: options.force ? "off" : "on",
					authorizationParams: {
						audience: getAuth0Audience(),
						scope: AUTH_SCOPE,
					},
				});
				if (!isSameRefreshContext(getRefreshContext(), initialContext)) {
					return null;
				}
				useAuthStore.getState().setTokens({
					accessToken: token,
					refreshToken: null,
					authMethod: "auth0-spa",
				});
				return token;
			}
		} catch {
			if (isSameRefreshContext(getRefreshContext(), initialContext)) {
				useAuthStore.getState().reset();
			}
			return null;
		}

		return null;
	})();

	refreshPromise = { context: initialContext, promise };
	promise.finally(() => {
		if (refreshPromise?.promise === promise) {
			refreshPromise = null;
		}
	});
	return promise;
}

function getRefreshContext(): RefreshContext {
	const { accessToken, refreshToken, authMethod } = useAuthStore.getState();
	return { accessToken, refreshToken, authMethod };
}

function isSameRefreshContext(a: RefreshContext, b: RefreshContext): boolean {
	return (
		a.accessToken === b.accessToken &&
		a.refreshToken === b.refreshToken &&
		a.authMethod === b.authMethod
	);
}

export async function logout() {
	const method = useAuthStore.getState().authMethod;
	useAuthStore.getState().reset();
	if (method === "auth0-spa") {
		const client = await getAuth0Client();
		await client.logout({
			logoutParams: { returnTo: window.location.origin },
		});
	}
}
